import CaseCustomCoCounselModal from "@/components/cases/case-custom-co-counsel-modal";
import CasePartyComplaintModal from "@/components/cases/case-party-complaint-modal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/core/accordion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Alert from "@/components/core/alert";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import DataGrid, { type ColumnDef } from "@/components/core/data-grid";
import { Form, FormField, FormGroup, FormItem, FormPrepend } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/core/hover-card";
import { useModal } from "@/components/core/modals/use-modal";
import { useCaseInvitePreviewMutation } from "@/features/case-record/api/queries/case-records-query";
import type { CaseMember } from "@/features/case-record/types/case-member";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import type { User } from "@/features/dashboard/types/user";
import { downloadFile } from "@/utils/download-file";
import { toOrdinal } from "@/utils/format-data";
import { IconAlertCircle, IconDots, IconKey } from "@tabler/icons-react";
import { IconPlus } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

interface CasePartyProps {
	type: "plaintiff" | "defendant";
	partyData: { id: number; name: string }[];
	counselData: { id: number; name: string }[];
	caseMember: CaseMember;
	case: CaseRecord;
	caseData: CaseRecord;
	showInvitationCode: boolean;
}

const userInformationColumns: ColumnDef<User>[] = [
	{
		id: "first_name",
		label: "Full Name",
		cell: ({ data }) => (
			<span>
				{data?.first_name || "N/A"} {data?.last_name}
			</span>
		),
	},
	{
		id: "email",
		label: "Email Address",
		cell: ({ data }) => <span>{data?.email || "N/A"}</span>,
	},
	{
		id: "law_firm",
		label: "Law Firm",
		cell: ({ data }) => <span>{data?.law_firm || "N/A"}</span>,
	},
	{
		id: "law_firm_email",
		label: "Law Firm Email ",
		cell: ({ data }) => <span>{data?.law_firm_email || "N/A"}</span>,
	},
	{
		id: "contact",
		label: "Contact Number",
		cell: ({ data }) => <span>{data?.contact || "N/A"}</span>,
	},
	{
		id: "address",
		label: "Address",
		cell: ({ data }) => <span>{data?.address || "N/A"}</span>,
	},
];

function UserInfoCard({
	user,
	invitationCode,
	caseData,
	member,
	showInvitationCode = true,
}: {
	user: User;
	invitationCode: string;
	caseData: CaseRecord;
	showInvitationCode: boolean;
}) {
	const caseInvitePreviewMutation = useCaseInvitePreviewMutation();
	const [isDownloading, setIsDownloading] = useState(false);
	const modal = useModal();
	const handleDownload = async () => {
		setIsDownloading(true);
		toast("Donwloading..");
		const response = await caseInvitePreviewMutation.mutateAsync({
			case_id: caseData.id,
		});

		const url = URL.createObjectURL(response);

		downloadFile(url, "invite-letter.pdf");
		setIsDownloading(false);
	};
	const form = useForm({
		defaultValues: {
			id: 1,
		},
	});

	return (
		<Form {...form}>
			<form className="w-full">
				<div className="flex flex-col w-full p-4 gap-2">
					<Alert type="success" allowClose={false} title="No related cases detected" icon={<IconAlertCircle size={20} stroke={2} />} />
					{showInvitationCode && (
						<div className="inline-flex flex-row items-center w-auto">
							<FormField
								control={form.control}
								name="code"
								render={({ field }) => (
									<FormItem>
										<FormGroup inputSize="sm">
											<FormPrepend>
												<IconKey size={18} stroke={2} />
											</FormPrepend>
											<Input className=" !rounded-none" autoComplete="off" readOnly defaultValue={invitationCode} {...field} />
										</FormGroup>
									</FormItem>
								)}
							/>
							<Button className="rounded-l-none" size="sm" type="button" loading={isDownloading} onClick={() => handleDownload()}>
								Download
							</Button>
						</div>
					)}
					<div className="flex flex-col w-full rounded-md border border-border/50 overflow-hidden">
						<DataGrid variant="compact" data={user} columns={userInformationColumns} />
					</div>
				</div>
			</form>
		</Form>
	);
}

export const CaseParty = ({ type, partyData, counselData, caseData, caseMember, count, showInvitationCode = true }: CasePartyProps) => {
	const isPlaintiff = type === "plaintiff";
	const badgeColor = isPlaintiff ? "blue" : "red";
	const partyLabel = isPlaintiff ? "Plaintiff" : "Defendant";
	const modal = useModal();
	return (
		<div className="flex flex-col w-full p-4 gap-2 border-b last:border-b-0 border-dashed border-border">
			<div className="flex flex-row w-auto gap-1 mb-2">
				<Badge size="sm" color={badgeColor}>
					{isPlaintiff ? "P" : "D"}
				</Badge>

				<Badge size="sm" color="gray-light">
					{count}
				</Badge>
			</div>

			<Accordion type="single" collapsible variant="compact">
				{partyData.map((member) => (
					<AccordionItem key={member.id} value="item-1">
						<AccordionTrigger className="py-2 px-3">
							<div className="inline-flex flex-row items-center w-auto gap-1">
								<HoverCard key={member?.id} openDelay={300} closeDelay={300}>
									<HoverCardTrigger asChild>
										<Link
											onClick={(e) => {
												e.stopPropagation();
											}}
											to={`/profile/${member?.user?.id}?status=all`}
											target="_blank"
										>
											<button type="button" className="text-base font-semibold text-blue-600 hover:text-blue-500">
												{member?.user?.full_name}
											</button>
										</Link>
									</HoverCardTrigger>
									<HoverCardContent>
										<UserInfoCard user={member?.user} invitationCode={member?.invitation_code} caseData={caseData} showInvitationCode={showInvitationCode} />
									</HoverCardContent>
								</HoverCard>
								<span className="text-base text-foreground/70">({member.counsels.length} Counsels)</span>
							</div>
							<div className="inline-flex ml-auto">
								<DropdownMenu modal={false}>
									<DropdownMenuTrigger asChild>
										<Button
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
											}}
											className="!rounded-full !w-[24px] !h-[24px]"
											size="icon"
											variant="ghost"
										>
											<IconDots size={14} stroke={1.5} />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										{!isPlaintiff && (
											<DropdownMenuItem
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													modal.openModal({
														title: "Add Third Party Complaint",
														size: "lg",
														showHeader: true,
														showFooter: false,
														component: <CasePartyComplaintModal caseData={caseData} user={member} />,
													});
												}}
											>
												<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
													<IconPlus size={16} stroke={2} />
													Add {toOrdinal(Number.parseInt(member._depth) + 3)} Party Complaint
												</div>
											</DropdownMenuItem>
										)}
										<DropdownMenuItem
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												const counselModal = modal.openModal({
													title: "Add New Counsel",
													description: "Case Counsel",
													size: "lg",
													component: (
														<CaseCustomCoCounselModal
															isEdit={false}
															caseData={caseData}
															isPlaintiffCounsel={isPlaintiff}
															defendants={partyData}
															onSubmit={(data) => {
																modal.closeModal(counselModal);
															}}
														/>
													),
												});
											}}
										>
											<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
												<IconPlus size={16} stroke={2} />
												<span className="">Add Counsel</span>
											</div>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</AccordionTrigger>
						<AccordionContent className="p-3">
							{member.counsels.length > 0 ? (
								<div className="flex flex-col w-full">
									<span className="text-base text-foreground/70">Counsels ({member.counsels.length})</span>
									<div className="flex flex-row flex-wrap">
										{member.counsels.map((counsel, index) => (
											<>
												<HoverCard key={counsel.id} openDelay={300} closeDelay={300}>
													<HoverCardTrigger asChild>
														<Link key={index} to={`/profile/${counsel?.user?.id}?status=all`} target="_blank">
															<button type="button" className="text-base font-semibold text-blue-600 hover:text-blue-500">
																{counsel?.user?.full_name}
															</button>
														</Link>
													</HoverCardTrigger>
													<HoverCardContent>
														<UserInfoCard
															user={counsel?.user}
															invitationCode={counsel?.invitation_code}
															showInvitationCode={showInvitationCode}
															caseData={caseData}
														/>
													</HoverCardContent>
												</HoverCard>

												{index < member.counsels.length - 1 && <span className="mr-1 text-foreground font-semibold">,</span>}
											</>
										))}
									</div>
								</div>
							) : (
								<span className="text-foreground/70 text-base">No counsel assigned</span>
							)}
							<span className="text-base text-foreground/70">{caseData.full_name}</span>
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>

			{/* {counselData.length > 0 && (
				<div className="flex flex-col w-auto">
					<span className="text-base text-foreground/70">
						Counsels ({counselData.length})
						<Button
							size="x-sm"
							className="ml-1"
							onClick={() => {
								const counselModal = modal.openModal({
									title: "Add New Counsel",
									description: "Case Counsel",
									size: "lg",
									component: (
										<CaseCustomCoCounselModal
											isEdit={false}
											caseData = { caseData }
											onSubmit={(data) => {
												modal.closeModal(counselModal);
											}}

										/>
									),
								});
							}}
						>
							<IconPlus size={12} stroke={2} />
							<span className=""></span>
						</Button>
					</span>
					<div className="flex flex-row flex-wrap">
						{counselData.map((counsel, index) => (
							<Link
								key={index}
								to={`/profile/${counsel?.user?.id}?status=all`}
								target="_blank"
							>
								<HoverCard key={counsel.id} openDelay={300} closeDelay={300}>
									<HoverCardTrigger asChild>
										<button
											type="button"
											className="mr-2 text-base font-semibold text-blue-600 hover:text-blue-500"
										>
											{counsel?.user?.first_name} {member?.user?.last_name}
										</button>
									</HoverCardTrigger>
									<HoverCardContent>
										<UserInfoCard
											user={counsel?.user}
											invitationCode={counsel?.invitation_code}
											showInvitationCode={showInvitationCode}
											caseData={caseData}
										/>
									</HoverCardContent>
								</HoverCard>
							</Link>
						))}
						<span className="text-base text-foreground/70">
							{caseData.counsel_first_name} {caseData.counsel_last_name}
						</span>
					</div>
				</div>
			)} */}
		</div>
	);
};
